import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  Heading1,
  JustifyContent,
  Link,
  LinkVariant,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import { getRenderableRichTextBlocks, RichText } from '../../components/RichText'
import type { HeroFields } from './types'

import { CtaLink, mapCmsCta } from '../../cta'
import type { OnTriggerPromoCode } from '../../cta'

export type HeroProps = HeroFields & {
  onTriggerPromocode?: OnTriggerPromoCode
}

const StyledRichTextLink = withCitrusTheme(Link, (theme) => ({
  color: theme.color(Color.Secondary_400),
  textDecoration: 'underline',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  fontFamily: 'inherit',
}))

function renderRichTextLink({
  children,
  href,
  target,
  key,
}: {
  children: React.ReactNode
  href: string
  target?: string
  key: React.Key
}) {
  return (
    <StyledRichTextLink key={key} variant={LinkVariant.None} href={href} target={target}>
      {children}
    </StyledRichTextLink>
  )
}

const RichTextListItem = withCitrusTheme('li', (theme) => ({
  color: theme.color(Color.ColdGrey_600),
}))

function renderRichTextListItem({ children, key }: { children: React.ReactNode; key: React.Key }) {
  return <RichTextListItem key={key}>{children}</RichTextListItem>
}

function HeroBody({ body }: { body?: HeroFields['body'] }) {
  if (getRenderableRichTextBlocks(body).length === 0) {
    return null
  }

  return (
    <Box paddingBottom={[4]} paddingRight={[null, 4]}>
      <RichText
        content={body}
        paragraphGapSize={4}
        renderParagraph={({ children, key }) => (
          <Body2 key={key} size={4} textAlign={TextAlign.Center} style={{ whiteSpace: 'pre-wrap' }}>
            {children}
          </Body2>
        )}
        renderLink={renderRichTextLink}
        renderListItem={renderRichTextListItem}
      />
    </Box>
  )
}

export function Hero({
  heading,
  body,
  cta,
  onTriggerPromocode,
  image,
  image_alt,
  image_mobile,
}: HeroProps) {
  const ctaLink = mapCmsCta(cta, onTriggerPromocode)

  return (
    <Box>
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        paddingH={[4, 8, 20]}
        paddingV={[8, 16]}
        paddingBottom={[12, 16]}
      >
        <Box maxWidth="512px">
          <Box paddingBottom={3}>
            <Heading1 textAlign={TextAlign.Center}>{heading}</Heading1>
          </Box>

          <HeroBody body={body} />

          {ctaLink && (
            <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
              <CtaLink cta={ctaLink} />
            </Box>
          )}
        </Box>
      </Box>

      {image?.url && (
        <ContentstackImage
          fit="cover"
          width="100%"
          height={['120px', '141px', null, '160px']}
          sizes="100vw"
          alt={image_alt || ''}
          src={image.url}
          mobileSrc={image_mobile?.url}
        />
      )}
    </Box>
  )
}
